import React from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  ComponentStateProvider,
  SnackbarProvider,
} from 'swipeclock-frontend';
import WhiteLabelProvider from 'providers/WhiteLabelProvider';
import { useUser } from 'store/userSlice';
import TimeWatcherProvider from './TimeWatcherProvider';
import OfflinePunchProvider from './OfflinePunchProvider';
import OfflinePunchDrawerGate from './components/OfflinePunchDrawerGate';
import PromptContainer from './components/PromptContainer';
import RouteTransition from './components/RouteTransition';
import WhiteLabelWrapper from './components/shared/WhiteLabelWrapper';
import ClockDashboard from './components/ClockDashboard';
import { getWebClockUserStorageKey } from './utils/webClockUserStorageKey';
import { useUnconfirmedSessionCleanup } from './hooks/useUnconfirmedSessionCleanup';
import {
  DevOfflineOverrideProvider,
  getDevIsOfflineOverride,
  useDevOfflineOverride,
} from './DevOfflineOverrideContext';

function OfflinePunchProviderWithDevOverride({
  children,
  userKey,
  onUnconfirmedSessionCleanup,
}) {
  const { devIsOffline } = useDevOfflineOverride();

  return (
    <OfflinePunchProvider
      key={userKey}
      devIsOfflineOverride={getDevIsOfflineOverride(devIsOffline)}
      onUnconfirmedSessionCleanup={onUnconfirmedSessionCleanup}
    >
      {children}
    </OfflinePunchProvider>
  );
}

OfflinePunchProviderWithDevOverride.propTypes = {
  children: PropTypes.node,
  userKey: PropTypes.string,
  onUnconfirmedSessionCleanup: PropTypes.func,
};

export default function Webclock() {
  const location = useLocation();
  const user = useUser();
  const userKey = getWebClockUserStorageKey(user);
  const handleUnconfirmedSessionCleanup = useUnconfirmedSessionCleanup();

  return (
    <ComponentStateProvider>
      <SnackbarProvider>
        <TimeWatcherProvider>
          <DevOfflineOverrideProvider>
            <OfflinePunchProviderWithDevOverride
              userKey={userKey}
              onUnconfirmedSessionCleanup={handleUnconfirmedSessionCleanup}
            >
              <WhiteLabelProvider>
                <WhiteLabelWrapper>
                  <OfflinePunchDrawerGate />
                  <RouteTransition location={location}>
                    <Switch>
                      <Route
                        path="/clock/web/pop-ups/punch/alert/failure"
                        render={(props) => (
                          <PromptContainer {...props} failure />
                        )}
                      />
                      <Route
                        path="/clock/web/pop-ups/punch/alert/success"
                        render={(props) => (
                          <PromptContainer {...props} success />
                        )}
                      />
                      <Route
                        path="/clock/web/pop-ups/punch/alert/warning"
                        render={(props) => (
                          <PromptContainer {...props} warning />
                        )}
                      />
                      <Route
                        path="/clock/web/pop-ups/prompts"
                        render={(props) => (
                          <PromptContainer {...props} clockPrompts />
                        )}
                      />
                      <Route
                        exact
                        path="/clock/web"
                        component={ClockDashboard}
                      />
                      <Redirect from="/" to="/clock/web" />
                    </Switch>
                  </RouteTransition>
                </WhiteLabelWrapper>
              </WhiteLabelProvider>
            </OfflinePunchProviderWithDevOverride>
          </DevOfflineOverrideProvider>
        </TimeWatcherProvider>
      </SnackbarProvider>
    </ComponentStateProvider>
  );
}

Webclock.propTypes = { enclosed: PropTypes.bool };
